import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Phone,
  PhoneDisconnect,
  Microphone,
  MicrophoneSlash,
  Spinner,
} from '@phosphor-icons/react';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { createCollectionActivity } from '../../../../../../redux/services/collectionActivityService';
import { createPromiseToPay } from '../../../../../../redux/services/promiseToPayService';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../../../general/AppDropDown/AppDropDown';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { BuyerContact, CallOutcome, CollectionActivityReference } from '@dill/dill-shared';
import LexicalEditor from '../../../../../general/LexicalEditor/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';
import {
  PromiseToPayFieldsSection,
  defaultPromiseToPayFieldsValue,
  isPromiseToPayFieldsValid,
  PromiseToPayFieldsValue,
} from '../PromiseToPayFieldsSection/PromiseToPayFieldsSection';
import './AddCallModal.scss';

interface AddCallModalProps {
  open: boolean;
  buyerId: string;
  phoneNumber?: string;
  callingMode?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialReferences?: CollectionActivityReference[];
  contacts?: BuyerContact[];
}

type CallStage = 'DIALING' | 'CONNECTED' | 'SUMMARIZING' | 'FORM';
type SummarizingPhase = 'TRANSCRIBING' | 'SUMMARIZING';

const callOutcomeOptions: { value: CallOutcome; label: string }[] = [
  { value: 'REACHED', label: 'Reached - Spoke with contact' },
  { value: 'VOICEMAIL', label: 'Voicemail - Left a message' },
  { value: 'NO_ANSWER', label: 'No Answer - No response' },
  { value: 'BUSY', label: 'Busy - Line was busy' },
  { value: 'FAILED', label: 'Failed - Call could not connect' },
];

function formatTimer(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

const fakeSummaries = [
  'Them: "Hi, this is Jessica from accounts payable."\n\nMe: "Hi Jessica, I\'m calling about the outstanding balance of $4,250 on your account. It\'s been about 30 days past due."\n\nThem: "Yes, I\'m aware of it. We\'ve been waiting on internal approval but I expect it to go through this week."\n\nMe: "Great, could we expect payment by Friday then?"\n\nThem: "I\'d say early next week to be safe."\n\nMe: "Okay, I\'ll make a note to follow up next Wednesday. Thanks for your time."',
  'Them: "Hello, this is David."\n\nMe: "Hi David, following up on invoice #2847 for $1,800. It was due on the 15th and I wanted to check on the status."\n\nThem: "Oh right, that one slipped through. Let me push it to the top of the queue."\n\nMe: "Appreciate it. Would you be able to process it this week?"\n\nThem: "Yeah, I\'ll get it done by Thursday."\n\nMe: "Sounds good, I\'ll check back on Friday if I don\'t see it come through. Have a good one."',
  'Them: "You\'ve reached the voicemail of Sarah Mitchell. Please leave a message."\n\nMe: "Hi Sarah, this is a follow-up call regarding the past-due balance of $6,750 on your account. We sent a reminder email last week but haven\'t heard back. Could you please give us a callback at your earliest convenience to discuss a payment timeline? You can reach us at the usual number. Thanks, have a great day."',
  'Them: "Accounts payable, this is Maria speaking."\n\nMe: "Hi Maria, I\'m calling about three overdue invoices totaling $9,300. The invoice numbers are 3012, 3056, and 3089."\n\nThem: "Let me pull those up... Okay, I see them. We actually processed payment for all three last Friday."\n\nMe: "That\'s great to hear. Would you be able to share the confirmation number?"\n\nThem: "Sure, it\'s PAY-88234. Should reflect in your system within 2-3 business days."\n\nMe: "Perfect, I\'ve noted that down. Thank you Maria."',
  'Them: "Hello?"\n\nMe: "Hi, may I speak with Robert about the outstanding balance on the account?"\n\nThem: "Robert\'s not available right now, but I can pass along a message."\n\nMe: "Sure, could you let him know we\'re following up on the $3,100 balance that\'s 45 days past due? We\'d like to set up a payment arrangement."\n\nThem: "I\'ll make sure he gets the message."\n\nMe: "Thanks, I\'ll try reaching him again on Thursday."',
];

function generateFakeSummary(): string {
  return fakeSummaries[Math.floor(Math.random() * fakeSummaries.length)];
}

function textToLexicalJson(text: string): string {
  const lines = text.split('\n');
  const children = lines.map((line) => ({
    type: 'paragraph',
    format: '',
    indent: 0,
    version: 1,
    children: line
      ? [{ type: 'text', text: line, format: 0, version: 1 }]
      : [{ type: 'text', text: '', format: 0, version: 1 }],
    direction: 'ltr',
    textFormat: 0,
    textStyle: '',
  }));
  return JSON.stringify({
    root: {
      type: 'root',
      format: '',
      indent: 0,
      version: 1,
      children,
      direction: 'ltr',
      textFormat: 0,
      textStyle: '',
    },
  });
}

function AddCallModal({
  open,
  buyerId,
  phoneNumber = '',
  callingMode = false,
  onClose,
  onSuccess,
  initialReferences,
  contacts,
}: AddCallModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [phone, setPhone] = useState(phoneNumber);
  const [duration, setDuration] = useState<number>(0);
  const [outcome, setOutcome] = useState<CallOutcome>('REACHED');
  const [notes, setNotes] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>(
    initialReferences ?? []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [promiseFields, setPromiseFields] = useState<PromiseToPayFieldsValue>(
    defaultPromiseToPayFieldsValue
  );

  // Calling mode state
  const [callStage, setCallStage] = useState<CallStage>('DIALING');
  const [isMuted, setIsMuted] = useState(false);
  const [summarizingPhase, setSummarizingPhase] = useState<SummarizingPhase>('TRANSCRIBING');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dialTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const summarizingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generatedSummaryRef = useRef<string>('');

  const isLoading = loadingList.includes('createCollectionActivity');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setPhone(phoneNumber);
      setDuration(0);
      setOutcome('REACHED');
      setNotes('');
      setReferences([]);
      setPhoneError('');
      setIsSubmitting(false);
      setPromiseFields(defaultPromiseToPayFieldsValue);
      if (callingMode) {
        setCallStage('DIALING');
        setIsMuted(false);
        setSummarizingPhase('TRANSCRIBING');
        generatedSummaryRef.current = '';
      }
    }
  }, [open, phoneNumber, callingMode]);

  // Auto-transition from DIALING to CONNECTED after 2.5s
  useEffect(() => {
    if (callingMode && callStage === 'DIALING' && open) {
      dialTimeoutRef.current = setTimeout(() => {
        setCallStage('CONNECTED');
      }, 2500);
    }
    return () => {
      if (dialTimeoutRef.current) clearTimeout(dialTimeoutRef.current);
    };
  }, [callingMode, callStage, open]);

  useEffect(() => {
    if (open && initialReferences) {
      setReferences(initialReferences);
    }
  }, [open, initialReferences]);

  // Timer while connected
  useEffect(() => {
    if (callingMode && callStage === 'CONNECTED') {
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [callingMode, callStage]);

  const handleEndCall = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Generate fake summary and show summarizing animation
    generatedSummaryRef.current = generateFakeSummary();
    setCallStage('SUMMARIZING');
    setSummarizingPhase('TRANSCRIBING');

    // Phase 1: Transcribing (1.5s) → Phase 2: Summarizing (2s) → FORM
    summarizingTimeoutRef.current = setTimeout(() => {
      setSummarizingPhase('SUMMARIZING');
      summarizingTimeoutRef.current = setTimeout(() => {
        setCallStage('FORM');
      }, 2000);
    }, 1500);
  }, []);

  const handleNotesChange = useCallback((content: string) => {
    setNotes(content);
  }, []);

  const handleSubmit = async () => {
    if (!phone.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }

    if (!currentSupplier?.id) {
      return;
    }
    if (!user?.id) {
      return;
    }

    if (!isPromiseToPayFieldsValid(promiseFields)) {
      dispatch(
        openMessageModal({
          title: 'Missing promise details',
          subTitle:
            'Please fill in the payment type, reason, and promised date, or uncheck "Customer promised to pay".',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(
        createCollectionActivity({
          supplierId: currentSupplier.id,
          activityData: {
            supplierId: currentSupplier.id,
            buyerId,
            activityType: 'PHONE_CALL',
            source: 'MANUAL',
            performedBy: user.id,
            date: new Date(),
            callDetails: {
              phoneNumber: phone,
              duration,
              outcome,
              notes: notes ? JSON.parse(notes) : undefined,
            },
            references: references.length > 0 ? references : undefined,
          },
        })
      );

      if (result.payload && (result.payload as any).success) {
        if (promiseFields.logPromise) {
          try {
            await dispatch(
              createPromiseToPay({
                supplierId: currentSupplier.id,
                data: {
                  supplierId: currentSupplier.id,
                  buyerId,
                  source: 'PHONE',
                  type: promiseFields.type || null,
                  reason: promiseFields.reason || null,
                  reasonOther:
                    promiseFields.reason === 'OTHER' ? promiseFields.reasonOther || null : null,
                  promisedPaymentDate: new Date(promiseFields.promisedPaymentDate),
                  amount: promiseFields.amount ? parseFloat(promiseFields.amount) : null,
                  references: references.length > 0 ? references : null,
                  notes: null,
                  status: 'ACTIVE',
                },
              })
            );
          } catch (promiseError) {
            console.error('Failed to log promise to pay:', promiseError);
            dispatch(
              openMessageModal({
                title: 'Call logged, but promise to pay failed',
                subTitle:
                  'The call was logged successfully, but we could not save the promise to pay. Please add it manually.',
                buttonText: 'OK',
                modalType: 'ERROR',
              })
            );
          }
        }
        setPhone(phoneNumber);
        setDuration(0);
        setOutcome('REACHED');
        setNotes('');
        setReferences([]);
        setPromiseFields(defaultPromiseToPayFieldsValue);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to log call:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to log call. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    if (callingMode && callStage === 'CONNECTED') {
      // End the call first, then they can dismiss from the form
      handleEndCall();
      return;
    }
    setPhone(phoneNumber);
    setDuration(0);
    setOutcome('REACHED');
    setNotes('');
    setReferences([]);
    setPhoneError('');
    setPromiseFields(defaultPromiseToPayFieldsValue);
    onClose();
  };

  const isCallingStage = callingMode && (callStage === 'DIALING' || callStage === 'CONNECTED');
  const isSummarizingStage = callingMode && callStage === 'SUMMARIZING';
  const isFormStage = !callingMode || callStage === 'FORM';

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title={isCallingStage ? 'Phone Call' : 'Log Phone Call'}
      maxWidth="576px"
      closeOnOverlayClick={!isCallingStage && !isSummarizingStage}
      footer={
        isFormStage ? (
          <>
            <DillButton
              onClick={handleClose}
              variant="secondary"
              isDisabled={isLoading}
              label="Cancel"
            />
            <DillButton
              onClick={handleSubmit}
              variant="primary"
              isLoading={isLoading}
              label="Log Call"
            />
          </>
        ) : undefined
      }>
      {/* DIALING stage */}
      {callingMode && callStage === 'DIALING' && (
        <div className="flex flex-col items-center py-8">
          <div className="relative flex items-center justify-center w-24 h-24 mb-6">
            <div className="add-call-pulse-ring absolute inset-0 rounded-full bg-blue-400" />
            <div className="add-call-pulse-ring add-call-pulse-ring-delayed absolute inset-0 rounded-full bg-blue-400" />
            <div className="relative z-10 w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
              <Phone size={28} weight="fill" className="text-white" />
            </div>
          </div>
          <p className="text-lg font-semibold text-gray-800 mb-1">Calling...</p>
          {phone && <p className="text-sm text-gray-500 mb-6">{phone}</p>}
          <DillButton onClick={handleClose} variant="secondary" label="Cancel" />
        </div>
      )}

      {/* CONNECTED stage */}
      {callingMode && callStage === 'CONNECTED' && (
        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
            <Phone size={28} weight="fill" className="text-white" />
          </div>
          <p className="text-lg font-semibold text-emerald-600 mb-1">Connected</p>
          {phone && <p className="text-sm text-gray-500 mb-4">{phone}</p>}
          <p className="text-3xl font-mono font-bold text-gray-800 tabular-nums mb-8">
            {formatTimer(duration)}
          </p>
          <div className="flex items-center gap-4">
            <DillButton
              onClick={() => setIsMuted(!isMuted)}
              variant={isMuted ? 'icon-red' : 'icon-default'}
              icon={isMuted ? <MicrophoneSlash size={20} /> : <Microphone size={20} />}
              width={48}
              height={48}
              className="!rounded-full"
            />
            <DillButton
              onClick={handleEndCall}
              variant="danger"
              icon={<PhoneDisconnect size={24} weight="fill" />}
              width={56}
              height={56}
              className="!rounded-full shadow-lg"
            />
          </div>
        </div>
      )}

      {/* SUMMARIZING stage */}
      {isSummarizingStage && (
        <div className="flex flex-col items-center py-8">
          <div className="relative flex items-center justify-center w-24 h-24 mb-6">
            <div className="add-call-pulse-ring absolute inset-0 rounded-full bg-violet-400" />
            <div className="add-call-pulse-ring add-call-pulse-ring-delayed absolute inset-0 rounded-full bg-violet-400" />
            <div className="relative z-10 w-16 h-16 rounded-full bg-violet-500 flex items-center justify-center">
              <Spinner size={28} className="text-white animate-spin" />
            </div>
          </div>
          <p className="text-lg font-semibold text-violet-600 mb-1">
            {summarizingPhase === 'TRANSCRIBING' ? 'Transcribing call...' : 'Summarizing call...'}
          </p>
          {phone && <p className="text-sm text-gray-500 mb-2">{phone}</p>}
          <p className="text-xs text-gray-400">
            {summarizingPhase === 'TRANSCRIBING'
              ? 'Converting speech to text'
              : 'Generating AI summary'}
          </p>
        </div>
      )}

      {/* FORM stage (default for non-calling mode, or after call ends) */}
      {isFormStage && (
        <div className="space-y-4">
          {/* Call summary banner when coming from calling mode */}
          {callingMode && callStage === 'FORM' && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Duration</span>
                <span className="text-sm font-medium text-gray-800">
                  {duration > 0 ? formatTimer(duration) : '0s'}
                </span>
              </div>
              {phone && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Phone</span>
                  <span className="text-sm font-medium text-gray-800">{phone}</span>
                </div>
              )}
            </div>
          )}

          <AppInputField
            label="Phone Number"
            isRequired
            id="phone"
            value={phone}
            onTextChange={(val) => {
              setPhone(val);
              if (phoneError) setPhoneError('');
            }}
            placeholder="+1 (555) 123-4567"
            isDisabled={isLoading || (callingMode && callStage === 'FORM')}
            errorText={phoneError}
          />

          <div className="grid grid-cols-2 gap-4">
            <AppInputField
              label="Duration (seconds)"
              id="duration"
              inputType="number"
              value={duration.toString()}
              onTextChange={(val) => setDuration(parseInt(val) || 0)}
              placeholder="0"
              isDisabled={isLoading || (callingMode && callStage === 'FORM')}
            />

            <AppDropDown
              label="Call Outcome"
              items={callOutcomeOptions}
              value={outcome}
              onSelectChange={(selected) => setOutcome(selected?.value as CallOutcome)}
              isDisabled={isLoading}
              placeholder="Select outcome"
            />
          </div>

          <LexicalEditor
            label="Call Notes (Optional)"
            initialContent={
              callingMode && generatedSummaryRef.current
                ? textToLexicalJson(generatedSummaryRef.current)
                : undefined
            }
            onChange={handleNotesChange}
            placeholder="Enter call notes..."
            minHeight="100px"
            editable={!isLoading}
          />

          <ActivityReferenceSelector
            label="References (Optional)"
            buyerId={buyerId}
            value={references}
            onChange={setReferences}
            isDisabled={isLoading}
            contacts={contacts}
          />

          <PromiseToPayFieldsSection
            value={promiseFields}
            onChange={setPromiseFields}
            isDisabled={isLoading}
          />
        </div>
      )}
    </DillModal>
  );
}

export default AddCallModal;
